// src/features/organisation/components/sites/SiteDetailForm.tsx
import React, { useMemo, useState } from 'react';
import { Building2, MapPin, Phone, Award } from 'lucide-react';
import { Site } from '../../../../types/organisation';
import { useSite, useSiteActions } from '../../hooks/useSites';
import { useSiteTypes } from '../../hooks/useReferenceData';
import { useSiteCertificationActions } from '../../hooks/useSiteCertifications';
import {
  EntityFormLayout,
  EntityFormHeader,
  EntityFormFooter,
  EditableField
} from '../../../../components/common/forms';
import { useEntityForm, FieldConfig, PendingChange } from '../../../../components/common/hooks';
import SiteCertificationManager from './SiteCertificationManager';

interface SiteDetailFormProps {
  siteId: number | null;
  onClose: () => void;
  onSave?: (site: Site) => void;
}

type BottomTabType = 'certifications';

const BOTTOM_TABS: { id: BottomTabType; label: string; icon: React.ElementType }[] = [
  { id: 'certifications', label: 'Certifications', icon: Award },
];

const SiteDetailForm: React.FC<SiteDetailFormProps> = ({
  siteId,
  onClose,
  onSave: onSaveCallback,
}) => {
  const { site, loading } = useSite(siteId);
  const { updateSite, createSite } = useSiteActions();
  const { siteTypes } = useSiteTypes();
  const { createCertification, updateCertification, deleteCertification } = useSiteCertificationActions();

  const [activeBottomTab, setActiveBottomTab] = useState<BottomTabType>('certifications');
  const [pendingCertificationChanges, setPendingCertificationChanges] = useState<PendingChange[]>([]);

  const fields: FieldConfig[] = useMemo(() => [
    {
      name: 'siteCode',
      label: 'Code du site',
      type: 'text',
      required: true,
      maxLength: 20,
      disabled: !!siteId,
    },
    {
      name: 'siteName',
      label: 'Nom du site',
      type: 'text',
      required: true,
      maxLength: 255,
    },
    {
      name: 'siteTypeCode',
      label: 'Type de site',
      type: 'select',
      required: true,
      options: siteTypes.map(type => ({ value: type.code, label: type.name })),
      render: (value) => siteTypes.find(t => t.code === value)?.name || value,
    },
    {
      name: 'isMainSite',
      label: 'Site principal',
      type: 'checkbox',
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      maxLength: 1000,
      colSpan: 2,
    },
    {
      name: 'address',
      label: 'Adresse',
      type: 'text',
      maxLength: 255,
      colSpan: 2,
    },
    {
      name: 'zipCode',
      label: 'Code postal',
      type: 'text',
      maxLength: 10,
    },
    {
      name: 'city',
      label: 'Ville',
      type: 'text',
      maxLength: 100,
    },
    {
      name: 'country',
      label: 'Pays',
      type: 'text',
      maxLength: 100,
    },
    {
      name: 'phone',
      label: 'Téléphone',
      type: 'tel',
      maxLength: 20,
    },
    {
      name: 'fax',
      label: 'Fax',
      type: 'tel',
      maxLength: 20,
    },
    {
      name: 'email',
      label: 'Email',
      type: 'email',
      maxLength: 255,
    },
  ], [siteTypes, siteId]);

  const transformToFormData = (s: Site | null) => {
    if (!s) return {
      siteCode: '', siteName: '', siteTypeCode: '', isMainSite: false,
      description: '', address: '', zipCode: '', city: '', country: 'France',
      phone: '', fax: '', email: '',
    };
    return {
      siteCode: s.siteCode,
      siteName: s.siteName,
      siteTypeCode: s.siteType.code,
      isMainSite: s.isMainSite,
      description: s.description || '',
      address: s.address || '',
      zipCode: s.zipCode || '',
      city: s.city || '',
      country: s.country || 'France',
      phone: s.phone || '',
      fax: s.fax || '',
      email: s.email || '',
    };
  };

  const handleSave = async (formData: Record<string, unknown>) => {
    let savedSite: Site;
    if (siteId) {
      savedSite = await updateSite(siteId, formData);
    } else {
      savedSite = await createSite(formData);
    }

    const targetId = siteId || savedSite.id;
    const certErrors: string[] = [];
    for (const change of pendingCertificationChanges) {
      try {
        if (change.type === 'add' && change.data) {
          await createCertification(targetId, change.data);
        } else if (change.type === 'modify' && change.data && change.id) {
          await updateCertification(targetId, change.id, change.data);
        } else if (change.type === 'remove' && change.id) {
          await deleteCertification(targetId, change.id);
        }
      } catch (err: unknown) {
        certErrors.push(err?.message || 'Erreur inconnue');
      }
    }
    setPendingCertificationChanges([]);

    if (certErrors.length > 0) {
      alert(`Site sauvegardé, mais erreur(s) sur les certifications :\n${certErrors.join('\n')}`);
    }

    onSaveCallback?.(savedSite);
    onClose();
    return savedSite;
  };

  const {
    formData,
    hasChanges: formHasChanges,
    saving,
    errors,
    handleFieldChange,
    startEditing,
    stopEditing,
    isEditing,
    handleSave: onFormSave,
  } = useEntityForm({
    entity: site,
    fields,
    onSave: handleSave,
    transformToFormData,
  });

  const hasChanges = formHasChanges || pendingCertificationChanges.length > 0;
  const totalPendingChanges = pendingCertificationChanges.length;

  const handleCertificationChange = (change: { type: string; id?: number; data?: Record<string, unknown> }) => {
    setPendingCertificationChanges(prev => [
      ...prev,
      { type: change.type, section: 'certifications', id: change.certId, data: change.data }
    ]);
  };

  const infoFields = fields.filter(f => ['siteCode', 'siteName', 'siteTypeCode', 'isMainSite', 'description'].includes(f.name));
  const locationFields = fields.filter(f => ['address', 'zipCode', 'city', 'country'].includes(f.name));
  const contactFields = fields.filter(f => ['phone', 'fax', 'email'].includes(f.name));

  return (
    <EntityFormLayout loading={loading} maxWidth="4xl">
      <EntityFormHeader
        title={site ? site.siteName : 'Nouveau site'}
        subtitle={site ? `Code: ${site.siteCode}` : undefined}
        icon={Building2}
        hasChanges={hasChanges}
        saving={saving}
        onSave={onFormSave}
        onClose={onClose}
        badges={[{ show: (s) => s?.isMainSite, label: 'Site Principal' }]}
        entity={site}
      />

      <div className="flex-1 overflow-y-auto p-6">
        {formData && (
          <>
            {/* Informations générales */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Informations générales
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {infoFields.map(field => (
                  <div key={field.name} className={field.colSpan === 2 ? 'col-span-2' : ''}>
                    <EditableField
                      field={field}
                      value={formData[field.name]}
                      isEditing={isEditing(field.name)}
                      onStartEdit={() => startEditing(field.name)}
                      onStopEdit={() => stopEditing(field.name)}
                      onChange={(value) => handleFieldChange(field.name, value)}
                      error={errors[field.name]}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Localisation */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Localisation
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {locationFields.map(field => (
                  <div key={field.name} className={field.colSpan === 2 ? 'col-span-2' : ''}>
                    <EditableField
                      field={field}
                      value={formData[field.name]}
                      isEditing={isEditing(field.name)}
                      onStartEdit={() => startEditing(field.name)}
                      onStopEdit={() => stopEditing(field.name)}
                      onChange={(value) => handleFieldChange(field.name, value)}
                      error={errors[field.name]}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Phone className="h-5 w-5" />
                Contact
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {contactFields.map(field => (
                  <div key={field.name}>
                    <EditableField
                      field={field}
                      value={formData[field.name]}
                      isEditing={isEditing(field.name)}
                      onStartEdit={() => startEditing(field.name)}
                      onStopEdit={() => stopEditing(field.name)}
                      onChange={(value) => handleFieldChange(field.name, value)}
                      error={errors[field.name]}
                    />
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Onglets du bas */}
        {site && (
          <div className="mt-8">
            <div className="border-b">
              <nav className="flex space-x-4">
                {BOTTOM_TABS.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveBottomTab(tab.id)}
                    className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                      activeBottomTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <tab.icon className="h-4 w-4" />
                    {tab.label}
                    {tab.id === 'certifications' && (site.certifications?.length || 0) > 0 && (
                      <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                        activeBottomTab === tab.id ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {site.certifications.length}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
            <div className="mt-6">
              {activeBottomTab === 'certifications' && (
                <SiteCertificationManager
                  site={site}
                  onCertificationChange={handleCertificationChange}
                  pendingCertificationChanges={pendingCertificationChanges}
                />
              )}
            </div>
          </div>
        )}
      </div>

      <EntityFormFooter
        helpText="💡 Double-cliquez sur un champ pour le modifier."
        pendingChangesCount={totalPendingChanges}
      />
    </EntityFormLayout>
  );
};

export default SiteDetailForm;
